import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Tag,
  Grid,
} from 'antd';
import { SearchOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AMAZON_STATUSES, ORDER_STATUSES } from '../utils/orderPresentation';

const queryFields = [
  { value: 'orderNo', label: '三方订单号' },
  { value: 'platformOrderNo', label: '亚马逊订单号' },
  { value: 'customerId', label: '买家号' },
];
const fieldLabels = {
  storeName: '店铺',
  orderNo: '三方订单号',
  platformOrderNo: '亚马逊订单号',
  customerId: '买家号',
  receivingCountry: '国家',
  receivingName: '收货人',
  receivingPhone: '收货电话',
  logisticsType: '物流类型',
  carrier: '承运商',
  startDate: '开始日期',
  endDate: '结束日期',
};
const OrderSearch = ({
  filters,
  hasCustomStatus,
  onSearch,
  onReset,
  onResetView,
  loading,
}) => {
  const [form] = Form.useForm();
  const [expanded, setExpanded] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const queryField = Form.useWatch('queryField', form) || 'orderNo';
  useEffect(() => {
    const field =
      queryFields.find((item) => filters[item.value])?.value || 'orderNo';
    form.resetFields();
    form.setFieldsValue({
      ...filters,
      queryField: field,
      dateRange:
        filters.startDate && filters.endDate
          ? [dayjs(filters.startDate), dayjs(filters.endDate)]
          : undefined,
    });
  }, [filters, form]);
  const submit = (values) => {
    const { queryField, dateRange, ...rest } = values;
    const next = { ...rest };
    queryFields.forEach((field) => {
      next[field.value] = next[field.value]?.trim() || undefined;
    });
    if (dateRange?.length === 2) {
      next.startDate = dateRange[0].format('YYYY-MM-DD');
      next.endDate = dateRange[1].format('YYYY-MM-DD');
    }
    onSearch(next);
  };
  const applied = Object.entries(fieldLabels).filter(([key]) => filters[key]);
  const storeField = (
    <Form.Item label="店铺" name="storeName">
      <Input
        prefix={!isMobile && <span className="filter-store-label">店铺</span>}
        placeholder="全部店铺"
        allowClear
      />
    </Form.Item>
  );
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={submit}
      className="order-filters"
      initialValues={{ queryField: 'orderNo' }}
    >
      <div className="filter-primary">
        <Form.Item label="订单 / 买家">
          <div className="filter-query">
            <Form.Item name="queryField" noStyle>
              <Select aria-label="查询字段" options={queryFields} />
            </Form.Item>
            <Form.Item name={queryField} noStyle>
              <Input
                aria-label="查询关键词"
                placeholder="输入完整或部分编号"
                allowClear
              />
            </Form.Item>
          </div>
        </Form.Item>
        {!isMobile && storeField}
        <div className="filter-buttons">
          <Button htmlType="submit" icon={<SearchOutlined />} loading={loading}>
            <span className="query-button-label">查询</span>
          </Button>
          <Button
            type="text"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-controls="more-filters"
            icon={expanded ? <UpOutlined /> : <DownOutlined />}
          >
            {expanded ? '收起筛选' : '更多筛选'}
          </Button>
        </div>
      </div>
      <div id="more-filters" className="filter-more" hidden={!expanded}>
        <Row gutter={16}>
          {isMobile && <Col span={24}>{storeField}</Col>}
          {queryFields
            .filter((field) => field.value !== queryField)
            .map((field) => (
              <Col key={field.value} xs={24} sm={12} lg={8}>
                <Form.Item label={field.label} name={field.value}>
                  <Input allowClear />
                </Form.Item>
              </Col>
            ))}
          <Col xs={24} sm={12} lg={8}>
            <Form.Item label="订单状态" name="statusList">
              <Select
                mode="multiple"
                allowClear
                options={Object.entries(ORDER_STATUSES).map(
                  ([value, item]) => ({
                    value: Number(value),
                    label: item.text,
                  }),
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item label="物流状态" name="amzStatusList">
              <Select
                mode="multiple"
                allowClear
                options={Object.entries(AMAZON_STATUSES).map(
                  ([value, item]) => ({
                    value: Number(value),
                    label: item.text,
                  }),
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item label="创建日期" name="dateRange">
              <DatePicker.RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {[
            'receivingCountry',
            'receivingName',
            'receivingPhone',
            'logisticsType',
            'carrier',
          ].map((key) => (
            <Col key={key} xs={24} sm={12} lg={8}>
              <Form.Item label={fieldLabels[key]} name={key}>
                <Input allowClear />
              </Form.Item>
            </Col>
          ))}
        </Row>
        {onResetView && <Button type="link" onClick={onResetView}>恢复默认视图</Button>}
      </div>
      {applied.length > 0 ||
      hasCustomStatus ||
      filters.amzStatusList?.length ? (
        <div className="filter-applied">
          <span className="muted">当前条件</span>
          {applied.map(([key, label]) => (
            <Tag key={key}>
              {label}：{filters[key]}
            </Tag>
          ))}
          {hasCustomStatus &&
            filters.statusList?.map((value) => (
              <Tag key={'s' + value}>{ORDER_STATUSES[value].text}</Tag>
            ))}
          {filters.amzStatusList?.map((value) => (
            <Tag key={'a' + value}>物流：{AMAZON_STATUSES[value].text}</Tag>
          ))}
          <Button type="link" size="small" onClick={onReset}>
            清除条件
          </Button>
        </div>
      ) : null}
    </Form>
  );
};
export default OrderSearch;
